import React, { useState, useMemo } from 'react';
import {
  MOIS_COURT, STATUS_CYCLE, STATUS_LABELS,
  STATUS_CELL_CLASS, STATUS_ABBREV, NEEDS_MOTIF,
  moisActifs, initials, formatMontant, exportCSV
} from '../utils/helpers';

const STATUS_OPTIONS = [
  { status: 'paye',             label: '✅ Payé',             bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  { status: 'defaut_provision', label: '⚠️ Défaut paiement', bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  { status: 'arret_adhesion',   label: '🛑 Arrêt adhésion',  bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  { status: 'autre_motif',      label: '📝 Autre motif',     bg: '#f5f5f5', color: '#555',    border: '#e0e0e0' },
];

const PAY_TABS = [
  { id: '',           label: 'Tous',        filter: () => true },
  { id: 'prelev_1',   label: 'Prél. J1',    filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '1' },
  { id: 'prelev_3',   label: 'Prél. J3',    filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '3' },
  { id: 'prelev_5',   label: 'Prél. J5',    filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '5' },
  { id: 'prelev_10',  label: 'Prél. J10',   filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '10' },
  { id: 'prelev_30',  label: 'Prél. J30',   filter: a => a.modePaiement === 'Prélèvement auto' && (!a.periodicite || a.periodicite === 'Mensuelle') && String(a.jourPrelevement) === '30' },
  { id: 'trimestre',  label: 'Trimestriel', filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Trimestrielle' },
  { id: 'semestre',   label: 'Semestriel',  filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Semestrielle' },
  { id: 'annuel_per', label: 'Annuel',      filter: a => a.modePaiement === 'Prélèvement auto' && a.periodicite === 'Annuelle' },
  { id: 'virement',   label: 'Virement',    filter: a => a.modePaiement === 'Virement' },
  { id: 'cheque',     label: 'Chèque',      filter: a => a.modePaiement === 'Chèque' },
];

export default function TableauAnnuel({ adherents, etablissements, cotisations, selectedYear, setYear, setCotisation, goToAdherent }) {
  const [filterEtab, setFilterEtab] = useState('');
  const [filterPay, setFilterPay] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [statusModal, setStatusModal] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [motifText, setMotifText] = useState('');

  const yearCots = cotisations[selectedYear] || {};

  const currentPayTab = PAY_TABS.find(t => t.id === filterPay) || PAY_TABS[0];

  const filtered = useMemo(() => {
    return adherents.filter(adh => {
      if (filterEtab && adh.etablissementId !== filterEtab) return false;
      if (!currentPayTab.filter(adh)) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const etab = etablissements.find(e => e.id === adh.etablissementId)?.nom || '';
        if (!(adh.prenom+' '+adh.nom+' '+adh.metier+' '+etab+' '+adh.numero).toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [adherents, etablissements, filterEtab, filterPay, filterSearch]); // eslint-disable-line

  const getStatus = (adhId, month) => {
    const c = (yearCots[adhId] || {})[month];
    return c ? c.status : 'non_pointe';
  };
  const getMotif = (adhId, month) => (yearCots[adhId] || {})[month]?.motif || '';

  const openStatusModal = (adh, month) => {
    setStatusModal({ adh, month });
    setMotifText(getMotif(adh.id, month));
    setPendingStatus(null);
  };

  const applyStatus = (status, motif = '') => {
    if (!statusModal) return;
    setCotisation(selectedYear, statusModal.adh.id, statusModal.month, status, motif);
    setStatusModal(null); setMotifText(''); setPendingStatus(null);
  };

  const handleStatusChoice = (status) => {
    if (NEEDS_MOTIF.includes(status)) { setPendingStatus(status); }
    else { applyStatus(status); }
  };

  // Totals per month
  const monthTotals = useMemo(() => {
    return Array.from({ length: 12 }, (_, mi) => {
      const m = mi + 1;
      return filtered.reduce((sum, adh) => {
        const c = (yearCots[adh.id] || {})[m];
        return sum + (c && c.status === 'paye' ? (adh.montant || 0) : 0);
      }, 0);
    });
  }, [filtered, yearCots]);

  // Totals per adherent
  const adhTotals = useMemo(() => {
    const t = {};
    filtered.forEach(adh => {
      let paid = 0, count = 0;
      for (let m = 1; m <= 12; m++) {
        const c = (yearCots[adh.id] || {})[m];
        if (c && c.status === 'paye') { paid += (adh.montant || 0); count++; }
      }
      t[adh.id] = { paid, count };
    });
    return t;
  }, [filtered, yearCots]);

  const totalCollected = monthTotals.reduce((a, b) => a + b, 0);

  const totalAttendu = useMemo(() => {
    return filtered.reduce((sum, adh) => sum + (adh.montant || 0) * moisActifs(adh, yearCots).length, 0);
  }, [filtered, yearCots]);

  const recouvrementTaux = totalAttendu > 0 ? Math.round(totalCollected / totalAttendu * 100) : 0;

  const handleExport = () => {
    const header = ['N°','Prénom','Nom','Établissement','Métier','Mode',...MOIS_COURT,'Total payé'];
    const rows = [header];
    filtered.forEach(adh => {
      const etab = etablissements.find(e => e.id === adh.etablissementId)?.nom || '';
      const cells = Array.from({length:12},(_,mi) => STATUS_LABELS[getStatus(adh.id,mi+1)]);
      rows.push([adh.numero,adh.prenom,adh.nom,etab,adh.metier,adh.modePaiement,...cells,formatMontant(adhTotals[adh.id]?.paid||0)]);
    });
    exportCSV(rows, `tableau_annuel_${selectedYear}.csv`);
  };

  const etabName = (id) => etablissements.find(e => e.id === id)?.nom || '—';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🗓️ Tableau annuel</div>
          <div className="page-subtitle">{filtered.length} adhérent{filtered.length>1?'s':''} · {formatMontant(totalCollected)} collectés</div>
        </div>
        <div className="flex gap-2">
          <select className="form-control" style={{ width:90 }} value={selectedYear} onChange={e => setYear(+e.target.value)}>
            {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ Export CSV</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
          <input className="form-control" style={{ flex:1, minWidth:160 }} placeholder="🔍 Recherche libre..."
            value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
          <select className="form-control" style={{ width:200 }} value={filterEtab} onChange={e => setFilterEtab(e.target.value)}>
            <option value="">Tous les établissements</option>
            {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
          </select>
          {(filterSearch||filterEtab||filterPay) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilterSearch(''); setFilterEtab(''); setFilterPay(''); }}>✕ Effacer</button>
          )}
        </div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {PAY_TABS.map(t => {
            const cnt = adherents.filter(t.filter).length;
            if (t.id !== '' && cnt === 0) return null;
            const active = filterPay === t.id;
            return (
              <button key={t.id} onClick={() => setFilterPay(t.id)} style={{
                padding:'5px 12px', borderRadius:20, border:'none', cursor:'pointer',
                background: active ? '#CC0000' : '#f0f0f0',
                color: active ? '#fff' : '#555',
                fontWeight: active ? 700 : 500, fontSize:12, transition:'all .15s',
                display:'flex', alignItems:'center', gap:5
              }}>
                {t.label}
                {t.id !== '' && <span style={{ background: active?'rgba(255,255,255,.3)':'#ddd', color: active?'#fff':'#777', borderRadius:10, fontSize:10, padding:'0 5px', fontWeight:700, lineHeight:'17px' }}>{cnt}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-3 mb-3" style={{ flexWrap:'wrap' }}>
        {STATUS_CYCLE.map(s => (
          <div key={s} className="flex items-center gap-2 text-xs text-gray">
            <span className={`month-cell ${STATUS_CELL_CLASS[s]}`} style={{ width:22, height:22, fontSize:10, cursor:'default' }}>
              {STATUS_ABBREV[s]}
            </span>
            {STATUS_LABELS[s]}
          </div>
        ))}
      </div>

      {/* Table */}
      {adherents.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">🗓️</div><div className="empty-state-text">Aucun adhérent. Chargez les données démo ou créez des adhérents.</div></div>
      ) : (
        <div className="card">
          <div className="table-wrap" style={{ maxHeight:'70vh', overflowY:'auto' }}>
            <table style={{ minWidth:900 }}>
              <thead>
                <tr>
                  <th style={{ position:'sticky', left:0, background:'#f8f8f8', zIndex:2, minWidth:160 }}>Adhérent</th>
                  <th style={{ minWidth:120 }}>Établissement</th>
                  {MOIS_COURT.map((m, mi) => (
                    <th key={mi} style={{ textAlign:'center', padding:'8px 4px', minWidth:36 }}>{m}</th>
                  ))}
                  <th style={{ textAlign:'right', minWidth:90 }}>Total payé</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(adh => (
                  <tr key={adh.id}>
                    <td style={{ position:'sticky', left:0, background:'#fff', zIndex:1 }}>
                      <div className="flex items-center gap-2">
                        <div className="adh-avatar" style={{ width:28, height:28, fontSize:11 }}>
                          {initials(adh.prenom, adh.nom)}
                        </div>
                        <div>
                          <div
                            style={{ fontWeight:600, fontSize:13, color:'#CC0000', cursor:'pointer', textDecoration:'underline', textDecorationColor:'#CC000044' }}
                            onClick={() => goToAdherent && goToAdherent(adh.id)}
                          >{adh.prenom} {adh.nom}</div>
                          <div className="text-xs text-gray">{adh.modePaiement}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize:12, color:'#555' }}>{etabName(adh.etablissementId)}</td>
                    {Array.from({ length:12 }, (_, mi) => {
                      const m = mi + 1;
                      const s = getStatus(adh.id, m);
                      const motif = getMotif(adh.id, m);
                      return (
                        <td key={mi} style={{ textAlign:'center', padding:'6px 3px' }}>
                          <div
                            className={`month-cell ${STATUS_CELL_CLASS[s]}`}
                            onClick={() => openStatusModal(adh, m)}
                            title={`${STATUS_LABELS[s]}${motif ? ' — '+motif : ''}`}
                            style={{ margin:'0 auto' }}
                          >
                            {STATUS_ABBREV[s]}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{ textAlign:'right', fontWeight:600, fontSize:13 }}>
                      {formatMontant(adhTotals[adh.id]?.paid || 0)}
                      <div className="text-xs text-gray">{adhTotals[adh.id]?.count || 0} pmt</div>
                    </td>
                  </tr>
                ))}
                {/* Month totals row */}
                <tr style={{ background:'#f8f8f8', fontWeight:700 }}>
                  <td colSpan={2} style={{ position:'sticky', left:0, background:'#f8f8f8' }}>TOTAL MENSUEL</td>
                  {monthTotals.map((t, mi) => (
                    <td key={mi} style={{ textAlign:'center', fontSize:11, padding:'6px 3px' }}>
                      {t > 0 ? formatMontant(t).replace('€','').trim() : '—'}
                    </td>
                  ))}
                  <td style={{ textAlign:'right' }}>{formatMontant(totalCollected)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-3 mt-3">
            <div className="text-sm text-gray" style={{ flexShrink:0 }}>Recouvrement global :</div>
            <div style={{ flex:1 }}>
              <div className="progress">
                <div className="progress-bar green" style={{ width:`${recouvrementTaux}%` }} />
              </div>
            </div>
            <div className="text-sm" style={{ whiteSpace:'nowrap' }}>
              <span className="font-bold">{formatMontant(totalCollected)}</span>
              <span className="text-gray"> / {formatMontant(totalAttendu)} · {recouvrementTaux}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Status modal */}
      {statusModal && (() => {
        const currentStatus = getStatus(statusModal.adh.id, statusModal.month);
        const closeModal = () => { setStatusModal(null); setPendingStatus(null); setMotifText(''); };
        return (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:400 }}>
              <div className="modal-header">
                <span className="modal-title">Saisie du pointage</span>
                <button className="btn-icon" onClick={closeModal}>✕</button>
              </div>
              <div style={{ padding:'4px 0 14px', color:'#888', fontSize:13 }}>
                {statusModal.adh.prenom} {statusModal.adh.nom} — {MOIS_COURT[statusModal.month - 1]} {selectedYear}
              </div>
              {!pendingStatus ? (
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:4 }}>
                  {STATUS_OPTIONS.map(opt => {
                    const isCurrent = currentStatus === opt.status;
                    return (
                      <button key={opt.status} onClick={() => {
                        if (isCurrent) { closeModal(); return; }
                        handleStatusChoice(opt.status);
                      }} style={{
                        padding:'12px 16px', borderRadius:8,
                        border: isCurrent ? `2px solid ${opt.border}` : `1px solid ${opt.border}`,
                        background: isCurrent ? opt.bg : '#fff',
                        color: opt.color, fontWeight: isCurrent ? 700 : 500, fontSize:14,
                        cursor:'pointer', transition:'all .15s', textAlign:'left',
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                      }}>
                        <span>{opt.label}</span>
                        {isCurrent && <span style={{ fontSize:11, opacity:.75 }}>✓ actuel</span>}
                      </button>
                    );
                  })}
                  <div style={{ borderTop:'1px solid #eeeeee', margin:'2px 0' }} />
                  {(() => {
                    const isNonPointe = currentStatus === 'non_pointe';
                    return (
                      <button onClick={() => {
                        if (isNonPointe) { closeModal(); return; }
                        applyStatus('non_pointe');
                      }} style={{
                        padding:'10px 16px', borderRadius:8,
                        border: isNonPointe ? '2px solid #bdbdbd' : '1px solid #e0e0e0',
                        background: isNonPointe ? '#f5f5f5' : '#fafafa',
                        color:'#757575', fontWeight: isNonPointe ? 700 : 500, fontSize:13,
                        cursor:'pointer', transition:'all .15s', textAlign:'left',
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                      }}>
                        <span>↩️ Non pointé (réinitialiser)</span>
                        {isNonPointe && <span style={{ fontSize:11, opacity:.75 }}>✓ actuel</span>}
                      </button>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  <div className="form-group">
                    <label className="form-label">Motif (optionnel)</label>
                    <textarea className="form-control" value={motifText} onChange={e => setMotifText(e.target.value)}
                      placeholder="Ex : chèque retourné, démission, congé parental..." rows={3} />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-outline" onClick={() => setPendingStatus(null)}>← Retour</button>
                    <button className="btn btn-primary" onClick={() => applyStatus(pendingStatus, motifText)}>Valider</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
